// KidFamilyPanel — kid-facing card shown on the Budget tab.
// Shows parent notes, allowance, goal boost, "Ask Mom/Dad" CTA, and
// the kid's own request history.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Heart, Wallet, HandHeart, Send, X, CheckCircle2, XCircle,
  Hourglass, Sparkles, Loader2,
} from "lucide-react";
import { Card } from "@/components/primitives";
import { fmtCAD } from "@/lib/budget";
import { familyAPI } from "@/lib/familyApi";

const KidFamilyPanel = ({ month, expenseCategories = [], variableTotal = 0 }) => {
  const [settings, setSettings] = useState(null);
  const [notes, setNotes] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [askOpen, setAskOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ amount: "", category: "", reason: "" });

  const reload = useCallback(async () => {
    try {
      const [s, n, a] = await Promise.all([
        familyAPI.getSettingsKid(),
        familyAPI.listNotes(),
        familyAPI.listApprovals(),
      ]);
      setSettings(s); setNotes(n.notes || []); setApprovals(a.approvals || []);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const goalBoost = useMemo(
    () => Number((settings?.goal_boosts || {})[month] || 0),
    [settings, month]
  );
  const allowance = Number(settings?.allowance_amount || 0);
  const allowanceFreq = settings?.allowance_frequency || "weekly";
  const kidName = settings?.kid_name || "Kid";
  const unreadNotes = notes.filter((n) => !n.read);

  const submitAsk = async () => {
    const amt = Number(form.amount);
    if (!amt || !form.reason.trim()) return;
    setBusy(true);
    try {
      await familyAPI.createApproval({
        amount: amt,
        category: form.category || null,
        reason: form.reason.trim(),
      });
      setForm({ amount: "", category: "", reason: "" });
      setAskOpen(false);
      reload();
    } finally {
      setBusy(false);
    }
  };

  const dismissNote = async (id) => {
    try { await familyAPI.markNoteRead(id); reload(); } catch (_) { /* ignore */ }
  };

  // Don't render anything until parent zone has been set up at least once
  // (kid hasn't met the family yet). Show a slim setup prompt instead.
  if (!settings) return null;

  const showFamilyPrompt = !settings.pin_set;

  return (
    <section className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8" data-testid="kid-family-panel">
      {/* Parent Notes (love letters!) */}
      {unreadNotes.length > 0 && (
        <Card className="p-5 md:col-span-12 border-[#F4D9A7] bg-gradient-to-r from-[#FFF7E8] to-[#FFFCF5]" testId="kid-parent-notes">
          <div className="flex items-center gap-2 mb-2">
            <Heart size={14} className="text-[#E27D60]" />
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#8A5A1A] font-medium">A note for you</p>
          </div>
          <div className="space-y-2">
            {unreadNotes.map((n) => (
              <div key={n.id} data-testid={`kid-note-${n.id}`} className="flex items-start gap-2 p-3 rounded-xl bg-white border border-[#F4D9A7]">
                <Sparkles size={14} className="text-[#E2B35B] mt-0.5 shrink-0" />
                <p className="flex-1 text-sm leading-relaxed">{n.text}</p>
                <button
                  data-testid={`dismiss-note-${n.id}`}
                  onClick={() => dismissNote(n.id)}
                  className="text-[#8A8C88] hover:text-[#1A1C1A] w-7 h-7 rounded-lg hover:bg-[#F4F6F1] flex items-center justify-center"
                  aria-label="Got it"
                  title="Got it!"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {showFamilyPrompt ? (
        <Card className="p-5 md:col-span-12" testId="family-not-setup">
          <p className="text-sm text-[#5A5C58]">
            <Heart size={12} className="inline mr-1 -mt-0.5 text-[#E27D60]" />
            Ask a parent to open the <b>Family</b> tab to set up allowance, spend limits, and notes.
          </p>
        </Card>
      ) : (
        <>
          {/* Allowance */}
          {allowance > 0 && (
            <Card className="p-5 md:col-span-4" testId="kid-allowance-card">
              <div className="flex items-center gap-2 mb-1">
                <Wallet size={14} className="text-[#5F7161]" />
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#5A5C58] font-medium">Allowance</p>
              </div>
              <p className="font-display text-2xl font-semibold text-[#5F7161]" data-testid="kid-allowance-amount">
                {fmtCAD(allowance)}
              </p>
              <p className="text-xs text-[#5A5C58] mt-1">per {allowanceFreq === "weekly" ? "week" : "month"}</p>
            </Card>
          )}

          {/* Goal Boost */}
          {goalBoost > 0 && (
            <Card className="p-5 md:col-span-4" testId="kid-goal-boost-card">
              <div className="flex items-center gap-2 mb-1">
                <HandHeart size={14} className="text-[#6B8E23]" />
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#5A5C58] font-medium">Parent boost</p>
              </div>
              <p className="font-display text-2xl font-semibold text-[#6B8E23]" data-testid="kid-goal-boost-amount">
                +{fmtCAD(goalBoost)}
              </p>
              <p className="text-xs text-[#5A5C58] mt-1">towards your goal this month 🎉</p>
            </Card>
          )}

          {/* Ask Mom/Dad CTA */}
          <Card className={`p-5 ${(allowance > 0 || goalBoost > 0) ? "md:col-span-4" : "md:col-span-12"}`} testId="kid-ask-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#5A5C58] font-medium">Need something?</p>
                <p className="font-display text-lg font-semibold mt-0.5">Ask {settings?.parent_name || "Mom/Dad"}</p>
              </div>
              <button
                data-testid="open-ask-form"
                onClick={() => setAskOpen((v) => !v)}
                className="px-4 py-2 rounded-full bg-[#E27D60] hover:bg-[#C66648] text-white text-sm font-medium transition-colors"
              >
                {askOpen ? "Cancel" : "Ask"}
              </button>
            </div>

            {askOpen && (
              <div className="mt-4 space-y-2" data-testid="ask-form">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-[#5A5C58] font-medium block mb-1">Amount</label>
                    <input
                      data-testid="ask-amount"
                      type="number" min="0" step="1"
                      value={form.amount}
                      onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                      placeholder="$"
                      className="w-full bg-white border border-[#D8D6D0] rounded-xl px-3 py-2 text-right font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161]"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-[#5A5C58] font-medium block mb-1">Category</label>
                    <select
                      data-testid="ask-category"
                      value={form.category}
                      onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                      className="w-full bg-white border border-[#D8D6D0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161]"
                    >
                      <option value="">— optional —</option>
                      {expenseCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-[#5A5C58] font-medium block mb-1">Why?</label>
                  <textarea
                    data-testid="ask-reason"
                    rows={2}
                    value={form.reason}
                    onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                    placeholder="e.g. School trip on Friday, $8 for the bus"
                    maxLength={240}
                    className="w-full resize-none bg-white border border-[#D8D6D0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161]"
                  />
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    data-testid="send-ask-btn"
                    onClick={submitAsk}
                    disabled={busy || !form.amount || !form.reason.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#5F7161] hover:bg-[#4E5C50] disabled:opacity-50 text-white text-sm font-medium"
                  >
                    {busy ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />}
                    Send to parent
                  </button>
                </div>
              </div>
            )}
          </Card>

          {/* Request history */}
          {approvals.length > 0 && (
            <Card className="p-5 md:col-span-12" testId="kid-requests-card">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#5A5C58] font-medium mb-3">
                {kidName}&apos;s requests
              </p>
              <div className="space-y-2">
                {approvals.slice(0, 5).map((a) => (
                  <div key={a.id} data-testid={`kid-request-${a.id}`} className="flex items-center justify-between gap-2 p-3 rounded-xl bg-[#F4F6F1] border border-[#E0E5DA]">
                    <div className="flex-1">
                      <p className="text-sm">
                        <span className="font-mono font-medium">{fmtCAD(a.amount)}</span>
                        {a.category ? <span className="text-[#5A5C58]"> · {a.category}</span> : null}
                        <span className="text-[#5A5C58]"> — {a.reason}</span>
                      </p>
                      {a.parent_note && <p className="text-xs text-[#5A5C58] italic mt-1">Parent: &quot;{a.parent_note}&quot;</p>}
                    </div>
                    {a.status === "pending" && (
                      <span className="flex items-center gap-1 text-xs text-[#8A5A1A] bg-[#FFF4E5] border border-[#F4D9A7] px-2.5 py-1 rounded-full">
                        <Hourglass size={11} /> waiting
                      </span>
                    )}
                    {a.status === "approved" && (
                      <span className="flex items-center gap-1 text-xs text-[#3D5B0F] bg-[#EDF3E0] border border-[#B6D77E] px-2.5 py-1 rounded-full">
                        <CheckCircle2 size={11} /> approved
                      </span>
                    )}
                    {a.status === "denied" && (
                      <span className="flex items-center gap-1 text-xs text-[#8A1F11] bg-[#FDECEA] border border-[#F6C5BD] px-2.5 py-1 rounded-full">
                        <XCircle size={11} /> denied
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </section>
  );
};

export default KidFamilyPanel;
